import { Box, Button, Typography } from "@mui/material";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/mode-css";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-xcode";
import "ace-builds/src-noconflict/ext-searchbox";
import { tokens } from "../tokens";

interface Props {
  /** Small uppercase caption above the editor, e.g. "HTML". */
  label?: string;
  mode: "html" | "css" | "json";
  value: string;
  onChange?: (value: string) => void;
  height?: string;
  readOnly?: boolean;
  showLineNumbers?: boolean;
  onFormat?: () => void;
  formatDisabled?: boolean;
}

/**
 * Replaces the hand-rolled "fake outlined input" wrapper used by the current
 * Edit screen: the caption sits above the box, so no absolutely positioned
 * label with a hardcoded background is needed.
 */
const CodeEditorPanel = ({
  label,
  mode,
  value,
  onChange,
  height = "210px",
  readOnly = false,
  showLineNumbers = true,
  onFormat,
  formatDisabled,
}: Props) => (
  <Box sx={{ width: "100%" }}>
    {label && (
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 700,
          color: tokens.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          mb: 1,
        }}
      >
        {label}
      </Typography>
    )}
    <Box
      sx={{
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
        backgroundColor: tokens.codeBg,
        overflow: "hidden",
        "& .ace_editor": { backgroundColor: "transparent" },
        "& .ace_gutter": { backgroundColor: "transparent" },
        "& .ace_active-line": { backgroundColor: "transparent" },
      }}
    >
      <AceEditor
        mode={mode}
        theme="xcode"
        name={`template-${mode}-editor`}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        width="100%"
        height={height}
        fontSize={12}
        showPrintMargin={false}
        showGutter={showLineNumbers}
        highlightActiveLine={!readOnly}
        setOptions={{
          enableBasicAutocompletion: false,
          enableLiveAutocompletion: false,
          enableSnippets: false,
          tabSize: 2,
          useWorker: false,
          wrap: true,
          fontFamily: tokens.monoFont,
          showLineNumbers,
          highlightGutterLine: false,
        }}
        editorProps={{ $blockScrolling: true }}
      />
    </Box>
    {onFormat && (
      <Button
        onClick={onFormat}
        disabled={formatDisabled}
        /* One tracker id for the action itself — the HTML, CSS and Additional
           Settings editors all report their Format click under it. */
        data-tracker="1079"
        startIcon={<FormatAlignCenterIcon sx={{ fontSize: 13 }} />}
        sx={{
          mt: 1.5,
          px: 1.5,
          py: 0.75,
          fontSize: 12,
          fontWeight: 700,
          color: tokens.accent,
          border: `1px solid ${tokens.accent}`,
          borderRadius: tokens.radius,
          textTransform: "uppercase",
          "&:hover": { backgroundColor: tokens.accentSoft },
        }}
      >
        Format
      </Button>
    )}
  </Box>
);

export default CodeEditorPanel;
